import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { RevenuePoint } from "@/features/dashboard/hooks/useDashboard";
import { currencySymbol, formatMoney } from "@/lib/format";

/**
 * Axis ticks stay compact without collapsing nearby values into the same label.
 * Keeping two decimals is useful for values such as 2 000 and 2 010, while the
 * currency symbol keeps the chart readable when it is viewed on its own.
 */
const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 2 });

function formatAxisMoney(value: number, currency: string): string {
  return `${compact.format(value)}\u00a0${currencySymbol(currency)}`;
}

function ChartTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: RevenuePoint }>;
  currency: string;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
      <p className="text-xs text-muted-foreground">{point.isToday ? "Aujourd’hui" : point.fullLabel}</p>
      <p className="text-sm font-semibold tabular-nums">{formatMoney(point.revenue, currency)}</p>
    </div>
  );
}

export function RevenueChart({ series, currency }: { series: RevenuePoint[]; currency: string }) {
  const total = series.reduce((sum, point) => sum + point.revenue, 0);

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-semibold">Recette des 7 derniers jours</h2>
        <p className="text-sm text-muted-foreground">
          Total <span className="font-semibold tabular-nums text-foreground">{formatMoney(total, currency)}</span>
        </p>
      </div>

      {total === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Aucune vente sur la période.</p>
      ) : (
        <div className="mt-4 h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
              <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "var(--muted-foreground)", fontSize: 13 }}
              />
              <YAxis
                width={68}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "var(--muted-foreground)", fontSize: 13 }}
                tickFormatter={(value: number) => formatAxisMoney(value, currency)}
              />
              <Tooltip cursor={{ fill: "var(--muted)" }} content={<ChartTooltip currency={currency} />} />
              <Bar dataKey="revenue" fill="var(--nafa-yellow)" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
